import { useEffect, useState } from 'react'
import { BsShieldLockFill } from 'react-icons/bs'
import { changePassword } from '@features/auth/api/authApi'
import { getAuthSession } from '@features/auth/session/authSession'
import { notify } from '@/shared/toaster/toast'
import { SaveButton } from '@shared/components/buttons'
import { vccCard, vccHeader, vccPage, vccUi } from '@shared/ui/vccLayout'

type UserCredentialsState = {
  userId: string
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

function RequiredLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label className="vcc-label col-form-label vcc-label--required" htmlFor={htmlFor}>
      {children}
    </label>
  )
}

export default function ChangePassword() {
  const [form, setForm] = useState<UserCredentialsState>({
    userId: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const userId =
      getAuthSession()?.user.userId ?? localStorage.getItem('UserId') ?? ''
    setForm((prev) => ({ ...prev, userId }))
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const validate = () => {
    if (!form.userId) {
      notify.warning('UserId required')
      return false
    }
    if (!form.oldPassword) {
      notify.warning('OldPassowrd required')
      return false
    }
    if (!form.newPassword) {
      notify.warning('NewPassword required')
      return false
    }
    if (!form.confirmPassword) {
      notify.warning('ConfirmPassword required')
      return false
    }
    if (form.oldPassword === form.newPassword) {
      notify.warning('Old and New Password cant be same')
      return false
    }
    if (form.newPassword !== form.confirmPassword) {
      notify.warning('Missmatch New Password and Old Password')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!validate()) return

    setIsSaving(true)
    try {
      const result = await changePassword(form)
      setForm((prev) => ({
        ...prev,
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      }))
      notify.success(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error changing password'
      notify.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={vccPage('change-password-page user-details-page')}>
      <article className={vccCard('user-details-card change-password-card')}>
        <header className={vccHeader('user-details-header')}>
          <BsShieldLockFill className="user-details-header__icon" aria-hidden />
          <div className="user-details-header__text">
            <h1 className="welcome-panel-header__title">Change password</h1>
            <p className="welcome-panel-header__subtitle">
              Update your account password securely
            </p>
          </div>
        </header>

        <form className={vccUi()} onSubmit={handleSubmit} noValidate>
          <div className="user-details-form-body change-password-form-body">
            <section className="user-details-form-section" aria-labelledby="change-password-account-heading">
              <h2 id="change-password-account-heading" className="user-details-form-section__title">
                Account
              </h2>

              <div className="vcc-form vcc-form--section">
                <div className="vcc-form-row vcc-form-row--field vcc-form-row--stacked">
                  <RequiredLabel htmlFor="user-id">User ID</RequiredLabel>
                  <div className="vcc-form-field">
                    <input
                      id="user-id"
                      className="form-control vcc-input"
                      name="userId"
                      value={form.userId}
                      onChange={handleChange}
                      maxLength={8}
                      placeholder="Enter user ID"
                      autoComplete="username"
                      required
                      aria-required="true"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="user-details-form-section" aria-labelledby="change-password-credentials-heading">
              <h2 id="change-password-credentials-heading" className="user-details-form-section__title">
                New credentials
              </h2>

              <div className="vcc-form vcc-form--section">
                <div className="user-details-form-grid user-details-form-grid--triple change-password-form-grid">
                  <div className="vcc-form-row vcc-form-row--field vcc-form-row--stacked">
                    <RequiredLabel htmlFor="old-password">Old password</RequiredLabel>
                    <div className="vcc-form-field">
                      <input
                        id="old-password"
                        type="password"
                        className="form-control vcc-input"
                        name="oldPassword"
                        value={form.oldPassword}
                        onChange={handleChange}
                        placeholder="Enter current password"
                        autoComplete="current-password"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>

                  <div className="vcc-form-row vcc-form-row--field vcc-form-row--stacked">
                    <RequiredLabel htmlFor="new-password">New password</RequiredLabel>
                    <div className="vcc-form-field">
                      <input
                        id="new-password"
                        type="password"
                        className="form-control vcc-input"
                        name="newPassword"
                        value={form.newPassword}
                        onChange={handleChange}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>

                  <div className="vcc-form-row vcc-form-row--field vcc-form-row--stacked">
                    <RequiredLabel htmlFor="confirm-password">Confirm password</RequiredLabel>
                    <div className="vcc-form-field">
                      <input
                        id="confirm-password"
                        type="password"
                        className="form-control vcc-input"
                        name="confirmPassword"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <footer className="user-details-form-actions">
            <SaveButton loading={isSaving} loadingLabel="Saving..." />
          </footer>
        </form>
      </article>
    </div>
  )
}
